import { Updater } from '@tanstack/react-table';
import { VisibilityState } from '@tanstack/table-core';
import { useCallback, useState } from 'react';
import { useUserConfigContext } from './useUserConfigContext';

const toVisibilityState = (hiddenColumns: string[] | undefined): VisibilityState | undefined => {
  if (!hiddenColumns) return undefined;
  return Object.fromEntries(hiddenColumns.map((column) => [column, false]));
};

const toHiddenColumns = (columnVisibility: VisibilityState): string[] =>
  Object.entries(columnVisibility)
    .filter(([, isVisible]) => !isVisible)
    .map(([column]) => column);

/**
 * Spaltensichtbarkeit einer Tabelle, gespeichert in der UserConfig.
 *
 * Gespeichert wird direkt im Setter statt in einem Effekt: Der Store pflegt
 * seinen lokalen Stand sofort und schreibt entprellt, deshalb braucht es hier
 * keinen Vergleich gegen den gespeicherten Wert mehr.
 */
export function useHiddenColumns(tableName: string, initial?: VisibilityState) {
  const userConfig = useUserConfigContext();

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    toVisibilityState(userConfig.getTableConfig(tableName)?.hiddenColumns) ?? initial ?? {},
  );

  const saveColumnVisibility = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      setColumnVisibility((previous) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(previous) : updaterOrValue;
        userConfig.upsertTableConfig(tableName, { hiddenColumns: toHiddenColumns(next) });
        return next;
      });
    },
    [tableName, userConfig],
  );

  return { columnVisibility, saveColumnVisibility };
}
